import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';

import { ICreateUpdateWebhookModuleInput } from 'graphql/types';
import { Button } from 'common/components/Button';
import { TextField, Switch } from 'common/components/FormField';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { Typography } from 'common/components/Typography';
import { IModuleFormValues } from 'training/types';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { Divider } from 'common/components/Divider';

export interface IWebhookModuleFormValues
    extends
        IModuleFormValues,
        Omit<ICreateUpdateWebhookModuleInput, 'imageId' | 'offset'> {}

export interface IProps {
    initialValues?: Partial<IWebhookModuleFormValues>;
    disabled?: boolean;
    edit?: boolean;
    onClose?(): void;
    onSubmit(values: IWebhookModuleFormValues): void;
    onDelete?(): void;
    moduleGroupId?: string;
}

export const WebhookModuleForm = ({
    initialValues,
    disabled,
    edit,
    moduleGroupId,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    const validationSchema = Yup.object().shape({
        externalRef: Yup.string().required(translate('validate.required')),
    });

    const initValues: IWebhookModuleFormValues = {
        triggerOnUpload: false,
        triggerOnCondition: false,
        externalRef: '',
        ...initialValues,
    };

    const formTitle = edit
        ? translate('moduleForm.webhook.editTitle')
        : translate('moduleForm.webhook.newTitle');
    const formText = edit
        ? translate('moduleForm.webhook.editText')
        : translate('moduleForm.webhook.newText');
    const submitText = edit
        ? translate('moduleForm.webhook.editButton')
        : translate('moduleForm.webhook.newButton');

    return (
        <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty, values }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />

                    <Form>
                        {(!!formTitle || !!formText) && (
                            <Box sx={{ mb: 2 }}>
                                {formTitle && (
                                    <Typography
                                        sx={{ fontWeight: 'bold' }}
                                        variant="h3"
                                    >
                                        {formTitle}
                                    </Typography>
                                )}

                                {formText && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography>{formText}</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Field
                            component={TextField}
                            label={translate(
                                'moduleForm.webhook.externalReference'
                            )}
                            name="externalRef"
                            sx={{ mb: 2 }}
                        />

                        <Field
                            checked={values.triggerOnUpload}
                            component={Switch}
                            helperText={translate(
                                'moduleForm.webhook.triggerOnUploadHelperText'
                            )}
                            label={translate(
                                'moduleForm.webhook.triggerOnUpload'
                            )}
                            name="triggerOnUpload"
                        />

                        <Field
                            checked={values.triggerOnCondition}
                            component={Switch}
                            helperText={translate(
                                'moduleForm.webhook.triggerOnConditionHelperText'
                            )}
                            label={translate(
                                'moduleForm.webhook.triggerOnCondition'
                            )}
                            name="triggerOnCondition"
                        />

                        <Divider sx={{ mt: 3 }} />

                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Box>
                                <Button
                                    color="primary"
                                    disabled={
                                        isSubmitting ||
                                        disabled ||
                                        (!isSubmitting && !disabled && !dirty)
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                    variant="contained"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();

                                        return submitForm();
                                    }}
                                >
                                    {submitText}
                                </Button>
                            </Box>
                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}
                            {onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting || disabled}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
